import { useMutation, useReactiveVar } from "@apollo/client";
import { RadioGroup } from "@headlessui/react";
import { ChangeEventHandler, useEffect, useState } from "react";
import {
  ComplianceProgramNotificationPreference,
  EmailPreference,
  GetAccountQuery,
  MeetingAgendaEmailPreference,
  MeetingSummaryEmailPreference,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Checkbox from "@components/checkbox";
import Heading from "@components/heading/heading";
import Loading from "@components/loading/loading";
import DeprecatedSelect from "@components/select/select";
import SettingsLayout from "@components/settings-layout/settings-layout";
import InfoTooltip from "@components/tooltip/info-tooltip";
import { classNames } from "@helpers/css";

import updateUserMutation from "../graphql/update-user-mutation";
import {
  getEffectiveNotificationPreference,
  getProgramNotificationSettings,
} from "./notification-settings";

const emailSettings = [
  {
    name: "Email notifications",
    value: "notification_emails",
    description: "Email me updates as things happen.",
  },
  {
    name: "No email notifications",
    value: "no_notification_emails",
    description: "No email notifications will be sent to you.",
  },
];

const meetingSummaryEmailSettings = [
  {
    name: "Never",
    value: "none",
    description: "Never send meeting summary emails.",
  },
  {
    name: "When not attending",
    value: "when_declined",
    description:
      "Send meeting summary emails if you decline the calendar event.",
  },
  {
    name: "Always",
    value: "always",
    description: "Always send a summary email after a meeting.",
  },
];

const meetingPreparationEmailSettings = [
  {
    name: "Never",
    value: "never",
    description: "Never send meeting agenda reminders.",
  },
  {
    name: "Missing agenda as organizer",
    value: "when_organizer",
    description:
      "Send agenda reminders when there is an agenda, or if you are the organizer of a meeting with no agenda",
  },
  {
    name: "When agenda exists",
    value: "when_agenda_exists",
    description: "Send agenda reminders when there is an agenda.",
  },
  {
    name: "Always",
    value: "always",
    description: "Always send agenda reminders.",
  },
];

const EmailSettingRadioOption = ({
  setting,
  isFirst,
  isLast,
}: {
  setting: { name: string; value: string; description?: string; info?: string };
  isFirst: boolean;
  isLast: boolean;
}) => {
  return (
    <RadioGroup.Option
      key={setting.name}
      value={setting.value}
      className={({ checked }) =>
        classNames(
          isFirst && "rounded-tl-md rounded-tr-md",
          isLast && "rounded-bl-md rounded-br-md",
          checked ? "bg-indigo-50 border-indigo-200" : "border-gray-200",
          "relative border p-4 flex cursor-pointer focus:outline-none",
        )
      }
    >
      {({ active, checked }) => (
        <>
          <span
            className={classNames(
              checked
                ? "bg-indigo-600 border-transparent"
                : "bg-white border-gray-300",
              active ? "ring-2 ring-offset-2 ring-indigo-500" : "",
              "h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center",
            )}
          >
            <span className="rounded-full bg-white w-1.5 h-1.5" />
          </span>
          <div className="ml-3 flex flex-col">
            <RadioGroup.Label
              as="span"
              className={classNames(
                checked ? "text-indigo-900" : "text-gray-900",
                "block text-sm font-medium",
              )}
            >
              <div className="flex items-center gap-2">
                <div>{setting.name}</div>
                {setting.info && <InfoTooltip text={setting.info} />}
              </div>
            </RadioGroup.Label>
            <RadioGroup.Description
              as="span"
              className={classNames(
                checked ? "text-indigo-700" : "text-gray-500",
                "block text-sm",
              )}
            >
              {setting.description}
            </RadioGroup.Description>
          </div>
        </>
      )}
    </RadioGroup.Option>
  );
};

const Email = ({ me }: { me: NonNullable<GetAccountQuery["me"]> }) => {
  const isAdmin = isAdminVar();
  const currentOrganization = useReactiveVar(currentOrganizationVar);
  const accountOrganization = me.organizations.edges.find(
    (edge) => edge?.node?.id === currentOrganization.id,
  )?.node;
  const programNotificationSettings = getProgramNotificationSettings(
    Boolean(accountOrganization?.hasSlackConnection),
    Boolean(accountOrganization?.hasTeamsNotificationConnection),
  );
  const label = useLabel();
  const [updateUser, { loading: isSavingUser }] =
    useMutation(updateUserMutation);
  const [emailPreference, setEmailPreference] = useState(me.emailPreference);
  const [meetingSummaryEmailPreference, setMeetingSummaryEmailPreference] =
    useState(me.meetingSummaryEmailPreference);
  const [
    meetingPreparationEmailPreference,
    setMeetingPreparationEmailPreference,
  ] = useState(me.meetingPreparationEmailPreference);
  const [enableDailySummaryEmails, setEnableDailySummaryEmails] = useState(
    me.enableDailySummaryEmails,
  );
  const [enableDailyCommitmentEmails, setEnableDailyCommitmentEmails] =
    useState(me.enableDailyCommitmentEmails);
  const [enableDraftReminderEmails, setEnableDraftReminderEmails] = useState(
    me.enableDraftReminderEmails,
  );
  const [programNotificationPreference, setProgramNotificationPreference] =
    useState(me.programNotificationPreference);
  const [surveyNotificationPreference, setSurveyNotificationPreference] =
    useState(me.surveyNotificationPreference);
  const [
    programAdminNotificationDownwardSubmitted,
    setProgramAdminNotificationDownwardSubmitted,
  ] = useState(me.programAdminNotificationDownwardSubmitted);
  const [primaryEmail, setPrimaryEmail] = useState(me.email);
  const effectiveProgramNotificationPreference =
    getEffectiveNotificationPreference(
      programNotificationPreference,
      programNotificationSettings,
    );
  const effectiveSurveyNotificationPreference =
    getEffectiveNotificationPreference(
      surveyNotificationPreference,
      programNotificationSettings,
    );

  const handleChangeEmailSetting = (value: EmailPreference) => {
    setEmailPreference(value);
    updateUser({
      variables: { userId: me.id, emailPreference: value },
    });
  };

  const handleChangeProgramNotificationPreference = (
    value: ComplianceProgramNotificationPreference,
  ) => {
    setProgramNotificationPreference(value);
    updateUser({
      variables: {
        userId: me.id,
        programNotificationPreference: value,
      },
    });
  };
  const handleChangeSurveyNotificationPreference = (
    value: ComplianceProgramNotificationPreference,
  ) => {
    setSurveyNotificationPreference(value);
    updateUser({
      variables: {
        userId: me.id,
        surveyNotificationPreference: value,
      },
    });
  };

  const handleChangeProgramAdminNotificationDownwardSubmitted = (
    value: boolean,
  ) => {
    setProgramAdminNotificationDownwardSubmitted(value);
    updateUser({
      variables: {
        userId: me.id,
        programAdminNotificationDownwardSubmitted: value,
      },
    });
  };

  const handleChangeMeetingSummaryEmailSetting = (
    value: MeetingSummaryEmailPreference,
  ) => {
    setMeetingSummaryEmailPreference(value);
    updateUser({
      variables: { userId: me.id, meetingSummaryEmailPreference: value },
    });
  };

  const handleChangeAgendaEmailSetting = (
    value: MeetingAgendaEmailPreference,
  ) => {
    setMeetingPreparationEmailPreference(value);
    updateUser({
      variables: { userId: me.id, meetingPreparationEmailPreference: value },
    });
  };

  const handleChangeDailySummaryEmailSetting: ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    setEnableDailySummaryEmails(event.target.checked);
    updateUser({
      variables: {
        userId: me.id,
        enableDailySummaryEmails: event.target.checked,
      },
    });
  };

  const handleChangeDailyCommitmentEmailSetting: ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    setEnableDailyCommitmentEmails(event.target.checked);
    updateUser({
      variables: {
        userId: me.id,
        enableDailyCommitmentEmails: event.target.checked,
      },
    });
  };

  const handleChangeDraftReminderEmailSetting: ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    setEnableDraftReminderEmails(event.target.checked);
    updateUser({
      variables: {
        userId: me.id,
        enableDraftReminderEmails: event.target.checked,
      },
    });
  };

  const handleChangePrimaryEmail = ({ value }: { value: string }) => {
    setPrimaryEmail(value);
    updateUser({
      variables: {
        userId: me.id,
        primaryEmailAddress: value,
      },
    });
  };

  useEffect(() => {
    setEmailPreference(me.emailPreference);
    setProgramNotificationPreference(me.programNotificationPreference);
    setSurveyNotificationPreference(me.surveyNotificationPreference);
    setMeetingPreparationEmailPreference(me.meetingPreparationEmailPreference);
    setEnableDailySummaryEmails(me.enableDailySummaryEmails);
    setEnableDailyCommitmentEmails(me.enableDailyCommitmentEmails);
    setMeetingSummaryEmailPreference(me.meetingSummaryEmailPreference);
    setEnableDraftReminderEmails(me.enableDraftReminderEmails);
    setPrimaryEmail(me.email);
  }, [me]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <Heading small title="Notifications" />
        {isSavingUser && <Loading mini size={5} />}
      </div>

      <SettingsLayout.Container>
        {me.emails.length > 1 && (
          <SettingsLayout.Row>
            <SettingsLayout.Left>
              Send email notifications to
            </SettingsLayout.Left>
            <SettingsLayout.Right>
              <DeprecatedSelect
                className="w-64"
                onChange={handleChangePrimaryEmail}
                value={primaryEmail}
                options={me.emails.map((email) => ({
                  value: email,
                  label: email,
                }))}
              />
            </SettingsLayout.Right>
          </SettingsLayout.Row>
        )}

        <SettingsLayout.Row>
          <SettingsLayout.Left>Event notifications</SettingsLayout.Left>
          <SettingsLayout.Right>
            <RadioGroup
              value={emailPreference}
              onChange={handleChangeEmailSetting}
            >
              <RadioGroup.Label className="sr-only">
                Email notification setting
              </RadioGroup.Label>
              <div className="bg-white rounded-md -space-y-px">
                {emailSettings.map((setting, settingIdx) => (
                  <EmailSettingRadioOption
                    key={setting.name}
                    isFirst={settingIdx === 0}
                    isLast={settingIdx === emailSettings.length - 1}
                    setting={setting}
                  />
                ))}
              </div>
            </RadioGroup>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>{`${label("review", {
            capitalize: true,
          })} notifications`}</SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="tf-header-sm mb-2">
              I want to receive notifications via...
            </div>
            <RadioGroup
              value={effectiveProgramNotificationPreference}
              onChange={handleChangeProgramNotificationPreference}
            >
              <RadioGroup.Label className="sr-only">
                {`${label("review", {
                  capitalize: true,
                })} notification preference`}
              </RadioGroup.Label>
              <div className="bg-white rounded-md -space-y-px">
                {programNotificationSettings.map((setting, settingIdx) => (
                  <EmailSettingRadioOption
                    key={setting.name}
                    isFirst={settingIdx === 0}
                    isLast={
                      settingIdx === programNotificationSettings.length - 1
                    }
                    setting={setting}
                  />
                ))}
              </div>
            </RadioGroup>
            {isAdmin && (
              <div className="mt-6">
                <div className="tf-header-sm mb-2">
                  As an Admin, I want to receive notifications when...
                </div>
                <Checkbox
                  label={`A manager submits a downward ${label("review")}`}
                  checked={!!programAdminNotificationDownwardSubmitted}
                  onChange={
                    handleChangeProgramAdminNotificationDownwardSubmitted
                  }
                />
              </div>
            )}
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Survey notifications</SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="tf-header-sm mb-2">
              I want to receive notifications via...
            </div>
            <RadioGroup
              value={effectiveSurveyNotificationPreference}
              onChange={handleChangeSurveyNotificationPreference}
            >
              <RadioGroup.Label className="sr-only">
                Survey notification preference
              </RadioGroup.Label>
              <div className="bg-white rounded-md -space-y-px">
                {programNotificationSettings.map((setting, settingIdx) => (
                  <EmailSettingRadioOption
                    key={setting.name}
                    isFirst={settingIdx === 0}
                    isLast={
                      settingIdx === programNotificationSettings.length - 1
                    }
                    setting={setting}
                  />
                ))}
              </div>
            </RadioGroup>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Daily commitments</SettingsLayout.Left>
          <SettingsLayout.Right className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!enableDailyCommitmentEmails}
              onChange={handleChangeDailyCommitmentEmailSetting}
            />{" "}
            Email me a preview of my day at 8am
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Daily summary</SettingsLayout.Left>
          <SettingsLayout.Right className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!enableDailySummaryEmails}
              onChange={handleChangeDailySummaryEmailSetting}
            />{" "}
            Email me a summary of my day at 5pm
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Agenda reminders</SettingsLayout.Left>
          <SettingsLayout.Right className="text-sm flex items-center gap-2">
            <RadioGroup
              value={meetingPreparationEmailPreference}
              onChange={handleChangeAgendaEmailSetting}
            >
              <RadioGroup.Label className="sr-only">
                Meeting Agenda Emails
              </RadioGroup.Label>
              <div className="bg-white rounded-md -space-y-px">
                {meetingPreparationEmailSettings.map((setting, settingIdx) => (
                  <EmailSettingRadioOption
                    key={setting.name}
                    isFirst={settingIdx === 0}
                    isLast={
                      settingIdx === meetingPreparationEmailSettings.length - 1
                    }
                    setting={setting}
                  />
                ))}
              </div>
            </RadioGroup>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Draft reminders</SettingsLayout.Left>
          <SettingsLayout.Right className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!enableDraftReminderEmails}
              onChange={handleChangeDraftReminderEmailSetting}
            />{" "}
            Remind me about unpublished draft meetings
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Meeting summary emails</SettingsLayout.Left>
          <SettingsLayout.Right>
            <RadioGroup
              value={meetingSummaryEmailPreference}
              onChange={handleChangeMeetingSummaryEmailSetting}
            >
              <RadioGroup.Label className="sr-only">
                Meeting Summary Emails
              </RadioGroup.Label>
              <div className="bg-white rounded-md -space-y-px">
                {meetingSummaryEmailSettings.map((setting, settingIdx) => (
                  <EmailSettingRadioOption
                    key={setting.name}
                    isFirst={settingIdx === 0}
                    isLast={
                      settingIdx === meetingSummaryEmailSettings.length - 1
                    }
                    setting={setting}
                  />
                ))}
              </div>
            </RadioGroup>
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      </SettingsLayout.Container>
    </div>
  );
};

export default Email;
